import { useCallback, useEffect, useRef } from "react";
import type { InputEvent, VmResolution } from "../types/input";
import { extractModifiers } from "../services/inputSerializer";

/** Minimum interval between mouse-move events (≈30 Hz).
 *  Input events go through the signaling WS (shared with WebRTC).
 *  30 Hz keeps latency acceptable without saturating the VM. */
const MOUSEMOVE_THROTTLE_MS = 33;

/** Default VM resolution when none is specified. */
const DEFAULT_VM_RESOLUTION: VmResolution = { width: 1920, height: 1080 };

const MODIFIER_KEYS = [
  { key: "Control", flag: "ctrlKey", codes: ["ControlLeft", "ControlRight"] },
  { key: "Shift", flag: "shiftKey", codes: ["ShiftLeft", "ShiftRight"] },
  { key: "Alt", flag: "altKey", codes: ["AltLeft", "AltRight"] },
  { key: "Meta", flag: "metaKey", codes: ["MetaLeft", "MetaRight"] },
] as const;

/**
 * Options for the useInputCapture hook.
 *
 * @param sendFn  - Callback invoked with each captured InputEvent.
 * @param vmResolution - Target VM screen resolution for coordinate normalisation.
 * @param enabled - Whether capture is active (default true).
 * @param pointerLockEnabled - Whether clicking the video activates pointer lock (FPS mode).
 * @param onPointerLockChange - Notified when pointer lock is acquired or released.
 */
export interface UseInputCaptureOptions {
  sendFn: (event: InputEvent) => void;
  vmResolution?: VmResolution;
  enabled?: boolean;
  pointerLockEnabled?: boolean;
  onPointerLockChange?: (locked: boolean) => void;
}

/**
 * React hook that captures mouse, keyboard and wheel events on a
 * video element and forwards them as normalised InputEvent objects.
 *
 * Coordinates are mapped from the CSS layout size of the <video>
 * element to the VM's native resolution. Mouse-move events are
 * throttled to a maximum of 60 per second.
 *
 * @param videoRef - React ref to the <video> element.
 * @param options  - Capture configuration.
 * @returns Object with the current sequence number.
 */
export function useInputCapture(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  options: UseInputCaptureOptions,
): { seq: number } {
  const {
    sendFn,
    vmResolution = DEFAULT_VM_RESOLUTION,
    enabled = true,
    pointerLockEnabled = false,
    onPointerLockChange,
  } = options;

  const seqRef = useRef(0);
  const lastMoveRef = useRef(0);

  // Stable reference to sendFn to avoid re-attaching listeners
  const sendRef = useRef(sendFn);
  sendRef.current = sendFn;

  const vmRef = useRef(vmResolution);
  vmRef.current = vmResolution;

  const pointerLockEnabledRef = useRef(pointerLockEnabled);
  pointerLockEnabledRef.current = pointerLockEnabled;

  const onPointerLockChangeRef = useRef(onPointerLockChange);
  onPointerLockChangeRef.current = onPointerLockChange;

  /** Get next sequence number. */
  const nextSeq = useCallback((): number => {
    seqRef.current += 1;
    return seqRef.current;
  }, []);

  /**
   * Normalise mouse coordinates from CSS element space to VM resolution.
   *
   * The <video> element may be scaled via CSS (object-fit: contain),
   * so we compute the ratio between the element's rendered size and
   * the VM's native resolution.
   */
  const normaliseCoords = useCallback(
    (
      offsetX: number,
      offsetY: number,
      el: HTMLVideoElement,
    ): { x: number; y: number } => {
      const vm = vmRef.current;
      const elWidth = el.clientWidth || 1;
      const elHeight = el.clientHeight || 1;

      // Compute the rendered video area within the element
      // (accounts for object-fit: contain letterboxing)
      const videoAspect = vm.width / vm.height;
      const elAspect = elWidth / elHeight;

      let renderWidth: number;
      let renderHeight: number;
      let offsetLeft: number;
      let offsetTop: number;

      if (elAspect > videoAspect) {
        // Pillarboxed (black bars on sides)
        renderHeight = elHeight;
        renderWidth = elHeight * videoAspect;
        offsetLeft = (elWidth - renderWidth) / 2;
        offsetTop = 0;
      } else {
        // Letterboxed (black bars top/bottom)
        renderWidth = elWidth;
        renderHeight = elWidth / videoAspect;
        offsetLeft = 0;
        offsetTop = (elHeight - renderHeight) / 2;
      }

      const relX = (offsetX - offsetLeft) / renderWidth;
      const relY = (offsetY - offsetTop) / renderHeight;

      return {
        x: Math.round(Math.max(0, Math.min(vm.width, relX * vm.width))),
        y: Math.round(Math.max(0, Math.min(vm.height, relY * vm.height))),
      };
    },
    [],
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !enabled) return;

    const now = (): number => performance.now();

    // Capture owns only keys forwarded from this video. A keyup may arrive
    // elsewhere after a focus change, or never arrive after an OS shortcut.
    const pressedKeys = new Map<string, { key: string; code: string }>();
    const emitKey = (t: "kd" | "ku", key: string, code: string, mod = 0): void => {
      sendRef.current({ t, key, code, mod, ts: now(), seq: nextSeq() });
    };
    const releaseKeys = (): void => {
      for (const { key, code } of pressedKeys.values()) emitKey("ku", key, code);
      pressedKeys.clear();
    };
    const resetKeyboard = (): void => {
      releaseKeys();
      // Also clear modifiers left by an interrupted older client/connection.
      // These releases are idempotent and never toggle CapsLock/NumLock.
      for (const modifier of MODIFIER_KEYS) {
        for (const code of modifier.codes) emitKey("ku", modifier.key, code);
      }
    };
    const syncModifiers = (e: KeyboardEvent | MouseEvent, currentCode?: string): void => {
      for (const modifier of MODIFIER_KEYS) {
        const held = modifier.codes.filter((code) => pressedKeys.has(code));
        if (!e[modifier.flag]) {
          for (const code of held) {
            if (code === currentCode) continue;
            emitKey("ku", modifier.key, code);
            pressedKeys.delete(code);
          }
        } else if (held.length === 0 && !modifier.codes.some((code) => code === currentCode)) {
          // The user may already be holding Ctrl when entering the player.
          const code = modifier.codes[0];
          emitKey("kd", modifier.key, code, extractModifiers(e));
          pressedKeys.set(code, { key: modifier.key, code });
        }
      }
    };

    /** Check if pointer is currently locked to our video element. */
    const isLocked = (): boolean => document.pointerLockElement === video;

    // ── Mouse move (throttled) ──────────────────────────────────
    const onMouseMove = (e: MouseEvent): void => {
      const t = now();
      if (t - lastMoveRef.current < MOUSEMOVE_THROTTLE_MS) return;
      lastMoveRef.current = t;

      if (isLocked()) {
        // Pointer lock active: send relative deltas for FPS games
        sendRef.current({
          t: "mr",
          dx: Math.round(e.movementX),
          dy: Math.round(e.movementY),
          ts: t,
          seq: nextSeq(),
        });
      } else {
        const { x, y } = normaliseCoords(e.offsetX, e.offsetY, video);
        sendRef.current({ t: "mm", x, y, ts: t, seq: nextSeq() });
      }
    };

    // ── Mouse down/up ───────────────────────────────────────────
    const onMouseDown = (e: MouseEvent): void => {
      video.focus({ preventScroll: true });
      syncModifiers(e);
      // Request pointer lock on click when enabled and not yet locked
      if (pointerLockEnabledRef.current && !isLocked()) {
        video.requestPointerLock();
      }

      const { x, y } = isLocked()
        ? { x: 0, y: 0 }
        : normaliseCoords(e.offsetX, e.offsetY, video);
      sendRef.current({
        t: "md",
        x,
        y,
        btn: e.button,
        ts: now(),
        seq: nextSeq(),
      });
    };

    const onMouseUp = (e: MouseEvent): void => {
      const { x, y } = isLocked()
        ? { x: 0, y: 0 }
        : normaliseCoords(e.offsetX, e.offsetY, video);
      sendRef.current({
        t: "mu",
        x,
        y,
        btn: e.button,
        ts: now(),
        seq: nextSeq(),
      });
    };

    // ── Double-click ───────────────────────────────────────────
    const onDblClick = (e: MouseEvent): void => {
      e.preventDefault();
      const { x, y } = isLocked()
        ? { x: 0, y: 0 }
        : normaliseCoords(e.offsetX, e.offsetY, video);
      sendRef.current({
        t: "dc",
        x,
        y,
        btn: e.button,
        ts: now(),
        seq: nextSeq(),
      });
    };

    // ── Keyboard ────────────────────────────────────────────────
    // Let Ctrl/Cmd+V bubble untouched so the browser fires `paste`
    // (used by SessionView to navigate the VM browser to a clipboard URL).
    const isPasteShortcut = (e: KeyboardEvent): boolean =>
      (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v";

    const onKeyDown = (e: KeyboardEvent): void => {
      syncModifiers(e, e.code);
      if (isPasteShortcut(e)) return;
      e.preventDefault();
      pressedKeys.set(e.code || e.key, { key: e.key, code: e.code });
      sendRef.current({
        t: "kd",
        key: e.key,
        code: e.code,
        mod: extractModifiers(e),
        ts: now(),
        seq: nextSeq(),
      });
    };

    const onKeyUp = (e: KeyboardEvent): void => {
      const identity = e.code || e.key;
      const held = pressedKeys.get(identity);
      if (!held) {
        // A modifier already held on entry uses a left-hand fallback. Its
        // physical right-hand keyup still releases our synthetic left key.
        if (MODIFIER_KEYS.some((modifier) =>
          modifier.codes.some((code) => code === e.code)
          && modifier.codes.some((code) => pressedKeys.has(code)))) syncModifiers(e);
        return; // Local chat/forms never become remote input.
      }
      pressedKeys.delete(identity);
      syncModifiers(e, e.code);
      if (e.target === video) e.preventDefault();
      emitKey("ku", held.key, held.code, extractModifiers(e));
    };

    // ── Wheel ───────────────────────────────────────────────────
    const onWheel = (e: globalThis.WheelEvent): void => {
      e.preventDefault();
      const { x, y } = isLocked()
        ? { x: 0, y: 0 }
        : normaliseCoords(e.offsetX, e.offsetY, video);
      sendRef.current({
        t: "wh",
        x,
        y,
        dx: Math.round(e.deltaX),
        dy: Math.round(e.deltaY),
        ts: now(),
        seq: nextSeq(),
      });
    };

    // ── Pointer lock change listener ────────────────────────────
    const onPLChange = (): void => {
      onPointerLockChangeRef.current?.(document.pointerLockElement === video);
      if (!isLocked()) releaseKeys();
    };
    document.addEventListener("pointerlockchange", onPLChange);
    const onVisibilityChange = (): void => {
      if (document.hidden) releaseKeys();
    };

    // ── Attach listeners ────────────────────────────────────────
    video.addEventListener("mousemove", onMouseMove);
    video.addEventListener("mousedown", onMouseDown);
    video.addEventListener("mouseup", onMouseUp);
    video.addEventListener("dblclick", onDblClick);
    video.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp, true);
    video.addEventListener("blur", releaseKeys);
    video.addEventListener("focus", resetKeyboard);
    window.addEventListener("blur", releaseKeys);
    window.addEventListener("pagehide", releaseKeys);
    document.addEventListener("visibilitychange", onVisibilityChange);
    video.addEventListener("wheel", onWheel, { passive: false });

    // Make video focusable for keyboard events
    if (!video.hasAttribute("tabindex")) {
      video.setAttribute("tabindex", "0");
    }

    // Prevent context menu on right-click
    const onContextMenu = (e: Event): void => {
      e.preventDefault();
    };
    video.addEventListener("contextmenu", onContextMenu);
    resetKeyboard();

    return () => {
      releaseKeys();
      video.removeEventListener("mousemove", onMouseMove);
      video.removeEventListener("mousedown", onMouseDown);
      video.removeEventListener("mouseup", onMouseUp);
      video.removeEventListener("dblclick", onDblClick);
      video.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp, true);
      video.removeEventListener("blur", releaseKeys);
      video.removeEventListener("focus", resetKeyboard);
      window.removeEventListener("blur", releaseKeys);
      window.removeEventListener("pagehide", releaseKeys);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      video.removeEventListener("wheel", onWheel);
      video.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("pointerlockchange", onPLChange);
      // Exit pointer lock on cleanup
      if (document.pointerLockElement === video) {
        document.exitPointerLock();
      }
    };
  }, [videoRef, enabled, normaliseCoords, nextSeq]);

  return { seq: seqRef.current };
}
